import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { hintsContainer, splitExceedsQty } from '../../../lib/packingListAllocation';

/**
 * 한 품목을 여러 컨테이너로 쪼갤 때 쓰는 수량 입력 다이얼로그. 컨테이너마다 카드로 수량을 받고
 * "Assign remaining"으로 나머지를 한 번에 채운다. 합계가 라인 수량을 넘으면 저장을 막고, 남는 수량은 미배분으로 남는다.
 * PL에 적힌 컨테이너는 카드에 표시한다.
 */
export default function AllocSplitInput({ open, item, containers, hints, values, saving, onChange, onSave, onCancel }) {
    if (!item) return null;
    const qty = Number(item.qty) || 0;
    const total = Object.values(values).reduce((a, v) => a + (Number(v) || 0), 0);
    const exceeds = splitExceedsQty(item, values);
    const remaining = qty - total;
    const sumSeverity = exceeds ? 'error' : remaining > 0 ? 'warning' : 'success';
    const sumLabel = exceeds ? 'Exceeds line quantity' : remaining === 0 ? 'Fully allocated' : `Remaining ${remaining.toLocaleString()}`;
    const code = item.buyer_sku || item.item_code || item.barcode || '—';

    const assignRemaining = (containerId) => {
        const others = Object.entries(values).filter(([k]) => Number(k) !== containerId).reduce((a, [, v]) => a + (Number(v) || 0), 0);
        onChange(containerId, Math.max(0, qty - others));
    };

    return (
        <Dialog open={open} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14, color: '#003a52', borderBottom: '1px solid #e6ebee', py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapHorizIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    Split quantity across containers
                </Box>
                <IconButton size="small" onClick={onCancel} disabled={saving}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '16px !important' }}>
                <Box sx={{ bgcolor: '#f8f8f8', borderRadius: 1, p: 1.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#003a52' }} noWrap>{item.description}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        SKU: {code} · Total qty: <b style={{ color: '#003a52' }}>{qty.toLocaleString()} {item.qty_uom}</b>
                    </Typography>
                    {hints.numbers.length > 0 && (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            {hints.fromLine ? 'Container on PL line' : 'Containers listed on PL'}: {hints.numbers.join(', ')}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                    {containers.map((c) => {
                        const onPl = hintsContainer(hints, c);
                        return (
                            <Box key={c.id} sx={{ border: '1px solid', borderColor: onPl ? '#00647e' : '#e6ebee', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#003a52' }}>
                                        {c.cntr_number} {c.volume && <span style={{ fontWeight: 400, color: '#6b7a82', fontSize: 11 }}>({c.volume})</span>}
                                        {onPl && <Chip size="small" label="On PL" sx={{ ml: 0.5, fontSize: 9, height: 16, bgcolor: '#00647e', color: 'white' }} />}
                                    </Typography>
                                    <Link component="button" underline="hover" disabled={saving} onClick={() => assignRemaining(c.id)} sx={{ fontSize: 11 }}>Assign remaining</Link>
                                </Box>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={values[c.id] === 0 || values[c.id] === undefined ? '' : values[c.id]}
                                    disabled={saving}
                                    onChange={(e) => onChange(c.id, e.target.value)}
                                    placeholder="0"
                                    inputProps={{ min: 0, max: qty }}
                                    InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{item.qty_uom}</Typography></InputAdornment> }}
                                    sx={{ '& input': { fontSize: 12, py: 0.75 }, '& .MuiInputBase-root': { bgcolor: '#fafafa' } }}
                                />
                            </Box>
                        );
                    })}
                </Box>
                <Alert severity={sumSeverity} icon={false} sx={{ py: 0.5, fontSize: 12, '& .MuiAlert-message': { width: '100%' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500 }}>
                        <span>Sum {total.toLocaleString()} / {qty.toLocaleString()} {item.qty_uom}</span>
                        <span>{sumLabel}</span>
                    </Box>
                    {exceeds && <Typography sx={{ fontSize: 11, mt: 0.5 }}>The sum is over the line quantity, so it cannot be saved. Adjust the quantities.</Typography>}
                </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #e6ebee', pt: 1.5 }}>
                <Button size="small" onClick={onCancel} disabled={saving} sx={{ fontSize: 12 }}>Cancel</Button>
                <Button size="small" variant="contained" disabled={exceeds || total === 0 || saving} onClick={onSave} sx={{ fontSize: 12 }}>
                    {saving ? 'Saving…' : 'Save split'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
